// Drag for the Forecast Manager when it holds stacks (the rail layout): one drag that works
// across every list on the board — Watchlist, Recent, and the cards of an open stack — with
// the same feel everywhere. Press a card (or a stack) and move to pick it up, or press and
// hold on touch; the cards of whichever list it is over slide aside to show where it will
// land; release to drop it there. Hold a card over the MIDDLE of another card (or a closed
// stack) to stack onto it instead. Esc cancels.
//
// Like sortableRows.js it moves the DOM nodes directly while dragging, so the list does not
// re-render per frame and the row components stay plain functions of their props. The drop is
// reported as an intent — onMove or onStack — and the caller decides what that changes.
import { sortShift } from "../lib/forecast/sortOrder.js";

const MOUSE_START_PX = 5;
const TOUCH_HOLD_MS = 350;
const TOUCH_SLOP_PX = 8;
const EDGE_PX = 40;
const EDGE_SPEED = 12;
const SLIDE = "transform 150ms ease";
/** Over the middle this share of a card, held this long, makes it a stack target. */
const STACK_BAND = 0.25;
const STACK_DWELL_MS = 300;

let session = null;

/**
 * Props for a draggable item's outer element.
 *
 * @param {string} id the card's id (a stack: its lead card's id)
 * @param {string} list "watchlist" | "recent" | `stack:<stackId>`
 * @param {{onMove: Function, onStack?: Function}} handlers
 *   `onMove(id, {list, beforeId, afterId, stackId})` — dropped into `list`, before `beforeId`
 *   (or after `afterId` at the end); `stackId` is set when a whole stack was dragged.
 *   `onStack(id, targetId)` — a card held over the middle of `targetId` and dropped.
 * @param {{stackId?: string|null}} [opts] set on a stack's own element: the whole stack drags
 */
export function boardRowProps(id, list, handlers, opts = {}) {
  return {
    "data-board-id": id,
    "data-board-list": list,
    "data-board-stack": opts.stackId || undefined,
    onPointerDown: (e) => press(e, id, list, handlers, opts.stackId || null),
  };
}

function press(e, id, list, handlers, stackId) {
  if (session || (e.pointerType === "mouse" && e.button !== 0)) return;
  // Controls keep their clicks — except a control marked as a drag handle (a stack's header).
  const control = e.target.closest("button, a, input, select, textarea, [role='button']");
  if (control && !control.hasAttribute("data-drag-handle")) return;
  const el = e.currentTarget;
  session = {
    id,
    list,
    handlers,
    stackId,
    el,
    touch: e.pointerType === "touch",
    startX: e.clientX,
    startY: e.clientY,
    lastY: e.clientY,
    active: false,
  };
  if (session.touch) {
    session.holdTimer = setTimeout(() => session && activate(), TOUCH_HOLD_MS);
  }
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onCancel);
  // telemetry-gesture: Escape cancels a drag in progress; the drop emits the reorder or stack intent
  window.addEventListener("keydown", onKey);
  // A pointer release is not guaranteed once the window loses focus: cancel, or the drag would
  // stay "on" and block every later one.
  window.addEventListener("blur", onCancel);
}

function scrollParent(el) {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === "auto" || oy === "scroll") return p;
  }
  return null;
}

/** An element's vertical span in the scroller's content space (CSS px, scroll-independent). */
function span(s, el) {
  const r = el.getBoundingClientRect();
  const top = (r.top - s.scrollerTop) / s.zoom + s.scrollStart;
  const h = r.height / s.zoom;
  return { top, bottom: top + h, mid: top + h / 2, h };
}

function activate() {
  const s = session;
  s.scroller = scrollParent(s.el);
  if (!s.scroller) return teardown(false);
  s.zoom = s.el.offsetHeight ? s.el.getBoundingClientRect().height / s.el.offsetHeight : 1;
  s.scrollerTop = s.scroller.getBoundingClientRect().top;
  s.scrollStart = s.scroller.scrollTop;
  // Every draggable item on the board except the dragged one and anything inside it.
  s.items = [...s.scroller.querySelectorAll("[data-board-list]")]
    .filter((r) => r !== s.el && !s.el.contains(r))
    .map((r) => ({
      el: r,
      id: r.dataset.boardId,
      list: r.dataset.boardList,
      isStack: !!r.dataset.boardStack,
      // The open stack a card is being dragged out of: still a place in its list, but it must
      // not slide, or it would carry the dragged card with it.
      holdsDragged: r.contains(s.el),
      ...span(s, r),
    }));
  // Open stacks a card can drop into (never a stack into a stack).
  s.stackAreas = s.stackId
    ? []
    : [...s.scroller.querySelectorAll("[data-board-stack-area]")].map((g) => ({
        list: g.dataset.boardStackArea,
        ...span(s, g),
      }));
  const boundary = s.scroller.querySelector("[data-board-boundary='recent']");
  s.recentTop = boundary ? span(s, boundary).top : Infinity;
  s.self = span(s, s.el);
  s.step = s.el.offsetHeight + (parseFloat(getComputedStyle(s.el).marginBottom) || 0);
  // The dragged item's slot in its own list, for closing the gap it leaves.
  s.origin = s.items.filter((it) => it.list === s.list);
  s.from = s.origin.filter((it) => it.mid < s.self.mid).length;
  s.dest = { list: s.list, t: s.from };
  s.candidate = null;
  s.stackTarget = null;
  s.active = true;
  for (const it of s.items) it.el.style.transition = SLIDE;
  s.el.style.transition = "none";
  s.el.style.zIndex = "10";
  s.el.style.filter = "drop-shadow(0 6px 14px rgba(0,0,0,0.45))";
  document.body.classList.add("sortable-dragging");
  window.addEventListener("touchmove", blockTouchScroll, { passive: false });
  s.raf = requestAnimationFrame(autoScroll);
  layout();
}

/** Which list the dragged item's center is over. */
function listAt(s, center) {
  const area = s.stackAreas.find((a) => center >= a.top && center <= a.bottom);
  if (area) return area.list;
  return center < s.recentTop ? "watchlist" : "recent";
}

function layout() {
  const s = session;
  const scrolled = s.scroller.scrollTop - s.scrollStart;
  const dy = (s.lastY - s.startY) / s.zoom + scrolled;
  s.el.style.transform = `translateY(${dy}px) scale(1.02)`;
  const center = s.self.mid + dy;
  // Over the MIDDLE of a card (where it is drawn now) the list holds still: that zone stacks.
  // The list only rearranges once the dragged card reaches a card's top or bottom quarter, so
  // the card you are aiming at doesn't slide away underneath you.
  let over = null;
  if (!s.stackId) {
    for (const it of s.items) {
      if (it.list.startsWith("stack:") || it.el.hasAttribute("data-board-stack-area")) continue;
      const shown = it.mid + (s.shifts?.get(it) || 0);
      if (Math.abs(center - shown) < it.h * STACK_BAND) over = it;
    }
  }
  // Left the card being aimed at: drop its target now, so the drop point below is live again
  // for this very move (a release right after must not commit the stale one).
  if (over !== s.candidate) {
    s.candidate = over;
    s.candidateSince = performance.now();
    setStackTarget(s, null);
  }
  if (!s.stackTarget && !over) {
    const list = listAt(s, center);
    const others = s.items.filter((it) => it.list === list);
    s.dest = { list, t: others.filter((it) => it.mid < center).length };
  }
  // Slide: the list it left closes its gap, the list it is over opens one at the drop point.
  const same = s.dest.list === s.list;
  const originIdx = new Map(s.origin.map((it, i) => [it, i]));
  const shifts = new Map();
  for (const it of s.items) {
    let shift = 0;
    if (same && it.list === s.list) {
      // Indexes with the dragged item put back at `from`, as sortShift expects.
      const i = originIdx.get(it);
      const j = i >= s.from ? i + 1 : i;
      shift = sortShift(j, s.from, s.dest.t, s.step);
    } else {
      if (it.list === s.list && originIdx.get(it) >= s.from) shift -= s.step;
      if (it.list === s.dest.list) {
        const k = s.items.filter((x) => x.list === s.dest.list).indexOf(it);
        if (k >= s.dest.t) shift += s.step;
      }
    }
    if (it.holdsDragged) shift = 0;
    shifts.set(it, shift);
    it.el.style.transform = shift ? `translateY(${shift}px)` : "";
  }
  s.shifts = shifts;
}

function setStackTarget(s, it) {
  if (s.stackTarget === it) return;
  if (s.stackTarget) {
    s.stackTarget.el.style.outline = "";
    s.stackTarget.el.style.outlineOffset = "";
  }
  s.stackTarget = it;
  if (it) {
    it.el.style.outline = "2px solid var(--c-accent)";
    it.el.style.outlineOffset = "2px";
  }
}

function autoScroll() {
  const s = session;
  if (!s || !s.active) return;
  if (s.candidate && !s.stackTarget && performance.now() - s.candidateSince >= STACK_DWELL_MS) {
    setStackTarget(s, s.candidate);
  }
  const r = s.scroller.getBoundingClientRect();
  const speed =
    s.lastY < r.top + EDGE_PX
      ? -EDGE_SPEED * Math.min(1, (r.top + EDGE_PX - s.lastY) / EDGE_PX)
      : s.lastY > r.bottom - EDGE_PX
        ? EDGE_SPEED * Math.min(1, (s.lastY - (r.bottom - EDGE_PX)) / EDGE_PX)
        : 0;
  if (speed) {
    const before = s.scroller.scrollTop;
    s.scroller.scrollTop = before + speed;
    if (s.scroller.scrollTop !== before) layout();
  }
  s.raf = requestAnimationFrame(autoScroll);
}

function onMove(e) {
  const s = session;
  if (!s) return;
  s.lastY = e.clientY;
  if (s.active) return layout();
  const dist = Math.hypot(e.clientX - s.startX, e.clientY - s.startY);
  if (s.touch) {
    if (dist > TOUCH_SLOP_PX) teardown(false);
  } else if (dist > MOUSE_START_PX) {
    activate();
  }
}

const onUp = () => teardown(true);
const onCancel = () => teardown(false);
function onKey(e) {
  if (e.key === "Escape" && session) {
    e.preventDefault();
    teardown(false);
  }
}
function blockTouchScroll(e) {
  if (session?.active) e.preventDefault();
}
function swallowClick(e) {
  e.stopPropagation();
  e.preventDefault();
}

function teardown(commit) {
  const s = session;
  session = null;
  clearTimeout(s?.holdTimer);
  window.removeEventListener("pointermove", onMove);
  window.removeEventListener("pointerup", onUp);
  window.removeEventListener("pointercancel", onCancel);
  window.removeEventListener("keydown", onKey);
  window.removeEventListener("blur", onCancel);
  window.removeEventListener("touchmove", blockTouchScroll);
  if (!s || !s.active) return;
  cancelAnimationFrame(s.raf);
  document.body.classList.remove("sortable-dragging");
  window.addEventListener("click", swallowClick, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("click", swallowClick, { capture: true }), 0);
  const target = commit ? s.stackTarget : null;
  setStackTarget(s, null);
  const destItems = s.items.filter((it) => it.list === s.dest.list);
  const moved = commit && !target && (s.dest.list !== s.list || s.dest.t !== s.from);
  const all = [...s.items.map((it) => it.el), s.el];
  if (target || moved) {
    // The list re-renders in its new arrangement; drop the transforms in the same frame with no
    // transition, so nothing animates back from where it was drawn.
    for (const el of all) {
      el.style.transition = "none";
      el.style.transform = "";
    }
    if (target) s.handlers.onStack?.(s.id, target.id);
    else {
      s.handlers.onMove(s.id, {
        list: s.dest.list,
        beforeId: destItems[s.dest.t]?.id ?? null,
        afterId: destItems[s.dest.t - 1]?.id ?? null,
        stackId: s.stackId,
      });
    }
    requestAnimationFrame(() => {
      for (const el of all) el.style.transition = "";
    });
  } else {
    for (const el of all) {
      el.style.transition = SLIDE;
      el.style.transform = "";
    }
    setTimeout(() => {
      for (const el of all) el.style.transition = "";
    }, 160);
  }
  s.el.style.zIndex = "";
  s.el.style.filter = "";
}
